import { cva } from 'class-variance-authority';

/**
 * Alert class recipes.
 *
 * Ported from the Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 639:77378) — 10 cells
 * across Severity x Mode, plus the `title` and `dismissable` booleans, every value read off the
 * exported vector.
 *
 * Zero `dark:` variants, as everywhere else in this layer: colour comes entirely from the
 * `--ds-alert-*` slots, which the `.ds-alert` / `.ds-alert-<severity>` classes in
 * `components/styles/design-system.css` set per theme.
 *
 * The geometry is uniform — there is no Size axis, and none of the values below changes with the
 * severity:
 *
 *   panel     1.5px border, 8px radius, 16px inset, 12px column gap
 *   icon      20px glyph, centred on the first text line
 *   content   4px row gap
 *   title     16/26 Medium   (Figma `Body 16 Med`)
 *   body      14/20 Regular  (Figma `Body S Reg`)
 *   dismiss   24px button carrying a 20px glyph
 *
 * Two of those depart from the file, both asked for after seeing it rendered:
 *
 *   - the border is **1.5px**, where the set draws 1px.
 *   - the icon is **centred on the first text line**, where the set top-aligns it. Figma's own
 *     render puts a 20px glyph at the top of a 26px title line box, so it sits 3px above that
 *     line's optical centre. `--ds-alert-line` below is how the centring is expressed.
 *
 * The set draws the panel 624px wide, which is the FRAME rather than the component: the page's
 * examples put the same alert at 720px, filling the form block above it. So the root is `w-full` and
 * the width belongs to the caller, as it does for the text input.
 */

/**
 * The panel.
 *
 * `items-start` is load-bearing once the description wraps: it keeps the icon and the dismiss control
 * beside the FIRST line rather than dragging them to the centre of a three-line block. The same
 * decision, for the same reason, as the checkbox's box against a wrapping label.
 *
 * `--ds-alert-line` is the height of that first line, and it is what the icon and the dismiss slot
 * centre themselves inside. It lives here rather than in either slot because both must agree: if the
 * two read different heights they stop being level with each other, which is more obvious than
 * either being wrong on its own. 26px is the title's line box, 20px the description's.
 *
 * `severity` selects a slot class rather than carrying colour itself, so this file has no hex values
 * and no theme branches at all. It has NO default: which severity is the design's default is an open
 * question on HNS-339 (the Figma set says Neutral; the shipped MUI wrapper defaults to a white
 * `info`), and defaulting either way here would answer it by accident. A call site must say what
 * kind of message it is showing.
 */
export const alertRecipe = cva(
  [
    'ds-alert',
    'flex w-full items-start gap-[12px]',
    'rounded-[8px] border-solid border-[length:1.5px] p-[16px]',
    'font-sans',
    'bg-[var(--ds-alert-bg)] border-[var(--ds-alert-border)] text-[var(--ds-alert-fg)]',
  ],
  {
    variants: {
      severity: {
        neutral: 'ds-alert-neutral',
        info: 'ds-alert-info',
        success: 'ds-alert-success',
        warning: 'ds-alert-warning',
        error: 'ds-alert-error',
      },
      /** Which line the glyphs centre on — see `--ds-alert-line` above. */
      hasTitle: {
        true: '[--ds-alert-line:26px]',
        false: '[--ds-alert-line:20px]',
      },
    },
    defaultVariants: { hasTitle: false },
  }
);

/**
 * The leading severity glyph.
 *
 * The one part of the alert with a colour of its own — everything else is `--ds-alert-fg`. That is
 * what makes severity legible without colour vision: the shape changes too (a triangle for Warning,
 * a circled × for Error, a tick for Success), so the hue is reinforcement rather than the signal.
 *
 * The box is 20px WIDE but as tall as the first text line, with the glyph centred inside it. That is
 * what puts the icon on the title's optical centre instead of its line-box top, and it is why the
 * height is a variable rather than a second `size-[20px]`: a fixed square would have to be nudged
 * with a margin that then went wrong the moment the title was absent.
 *
 * `[&_svg]:size-[20px]` rather than `size-full`, since the box is no longer square — `size-full`
 * would stretch the glyph to the line height.
 */
export const alertIconRecipe = cva([
  'flex h-[var(--ds-alert-line)] w-[20px] shrink-0 items-center justify-center',
  'text-[var(--ds-alert-icon)]',
  '[&_svg]:size-[20px]',
]);

/**
 * The text column.
 *
 * `min-w-0` and `flex-1` together are what let a long unbroken string (a URL, an event name) shrink
 * this column instead of pushing the dismiss control off the panel — a flex item's default
 * `min-width: auto` would otherwise refuse to go below its content's intrinsic width.
 *
 * `word-break: break-word` is the design's own setting on the container (node 639:77164), and it is
 * the half of the pair that handles a single word longer than the column.
 */
export const alertContentRecipe = cva(['flex min-w-0 flex-1 flex-col gap-[4px]', '[word-break:break-word]']);

/** Figma `Body 16 Med`. A step UP from the description, which is 14px Regular. */
export const alertTitleRecipe = cva(['m-0 text-[16px] leading-[26px] font-medium']);

/** Figma `Body S Reg`. Colour is inherited from the panel, so this sets none. */
export const alertDescriptionRecipe = cva(['m-0 text-[14px] leading-5 font-normal']);

/**
 * The slot the dismiss control sits in — NOT the control itself.
 *
 * The control is the layer's own `IconButton` at `size="2xs"` with `muted`/`ghost`, so the alert
 * does not hand-roll a button: it inherits the focus ring, the hover and pressed backgrounds, the
 * disabled treatment, the 24px box and the 20px glyph from the same recipe every other DS button
 * uses. An earlier build did hand-roll it, which meant reproducing the UA reset this layer needs
 * (preflight is deliberately omitted — see the header of `design-system.css`) and then keeping that
 * copy in step with `Button` by hand.
 *
 * `2xs` is that 24px cell, and it was added to the button scale for this control — see the scale
 * note in `Button.recipe` for why a new step rather than a smaller `xs`.
 *
 * `muted`/`ghost` is the only ghost the design draws, and its resting foreground is Dark/1 light /
 * Light/0 dark — the alert's own text colour, so the glyph looks exactly as it did before. What it
 * adds is a hover and pressed background of Light/1 / Dark/4, which on the four coloured panels
 * reads as a neutral tint rather than a tint of the panel's own hue. Accepted deliberately: the
 * design specifies no hover for this control, so a severity-tinted one would mean inventing five
 * colours.
 *
 * All this wrapper does is centre the button on the first text line, level with the severity icon
 * opposite.
 */
export const alertDismissRecipe = cva(['flex h-[var(--ds-alert-line)] shrink-0 items-center']);
